/* eslint-disable @atlassian/tangerine/import/no-parent-imports */

import React, { useCallback, useState } from 'react';

import { useIntl } from 'react-intl';

import Badge from '@atlaskit/badge';
import Button from '@atlaskit/button/new';
import DropdownMenu from '@atlaskit/dropdown-menu';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Stack, xcss } from '@atlaskit/primitives';

import { universalTypeFilterId } from '../../../../../common/constants/filters';
import { ProductKeys } from '../../../../../common/constants/products';
import type { SearchStoreExperience } from '../../../../../common/constants/search';
import { SearchExperience } from '../../../../../common/constants/search-experience';
import ClearFilterButton from '../../../../../common/ui/clear-filter-button';
import { FilterButton } from '../../../../../common/ui/filters/filter-button';
import { FilterButtonOld } from '../../../../../common/ui/filters/filter-button-old';
import { MaybeHoverOutWrapper } from '../../../../../common/ui/filters/hover-out-wrapper';
import { FILTER_CHANGE_LOCATION } from '../../../../../common/utils/filters/events';
import {
	useFeatures,
	useFiltersDiscoverability2Enabled,
	usePrimaryProduct,
} from '../../../../../controllers/store/bootstrap';
import {
	useAnyFilterSelected,
	useFilterDropdownOpenByQueryParam,
	useFiltersToDisplay,
	useSearchActions,
	useSelectedFilterCount,
	useSelectedProduct,
} from '../../../../../controllers/store/search';
import { watchedFilter } from '../../../../../controllers/store/search/filters';
import { filter as archived } from '../../../../../controllers/store/search/filters/archived';
import { filter as archivedAndPendingComponents } from '../../../../../controllers/store/search/filters/archived-and-pending-components';
import { filter as assignee } from '../../../../../controllers/store/search/filters/assignee';
import { filter as campaignStatus } from '../../../../../controllers/store/search/filters/campaign-status';
import { filter as caseStatus } from '../../../../../controllers/store/search/filters/case-status';
import { filter as closeDate } from '../../../../../controllers/store/search/filters/close-date';
import { filter as commentedByMe } from '../../../../../controllers/store/search/filters/commentedByMe';
import { filter as content } from '../../../../../controllers/store/search/filters/content';
import { filter as contributor } from '../../../../../controllers/store/search/filters/contributor';
import { filter as createdBy } from '../../../../../controllers/store/search/filters/created-by';
import { filter as focusAreaType } from '../../../../../controllers/store/search/filters/focus-area-type';
import { filter as focusAreaUnder } from '../../../../../controllers/store/search/filters/focus-area-under';
import { filter as label } from '../../../../../controllers/store/search/filters/label';
import { filter as lastUpdated } from '../../../../../controllers/store/search/filters/last-updated';
import { filter as lastUpdatedBy } from '../../../../../controllers/store/search/filters/last-updated-by';
import { filter as opportunityStatus } from '../../../../../controllers/store/search/filters/opportunity-status';
import { filter as owner } from '../../../../../controllers/store/search/filters/owner';
import { filter as pageStatus } from '../../../../../controllers/store/search/filters/page-status';
import { filter as positionEmploymentType } from '../../../../../controllers/store/search/filters/position-employment-type';
import { filter as positionFocusArea } from '../../../../../controllers/store/search/filters/position-focus-area';
import { filter as positionJobFamily } from '../../../../../controllers/store/search/filters/position-job-family';
import { filter as positionStatus } from '../../../../../controllers/store/search/filters/position-status';
import { filter as positionTeam } from '../../../../../controllers/store/search/filters/position-team';
import { filter as project } from '../../../../../controllers/store/search/filters/project';
import { filter as pullRequestStatus } from '../../../../../controllers/store/search/filters/pull-request-status';
import { filter as reporter } from '../../../../../controllers/store/search/filters/reporter';
import { filter as salesforceStage } from '../../../../../controllers/store/search/filters/salesforce-stage';
import { filter as slackChannel } from '../../../../../controllers/store/search/filters/slack-channel';
import { filter as space } from '../../../../../controllers/store/search/filters/space';
import { filter as statusFilter } from '../../../../../controllers/store/search/filters/status';
import { filter as titleMatchOnly } from '../../../../../controllers/store/search/filters/title-match-only';
import { filter as verified } from '../../../../../controllers/store/search/filters/verified';
import { filter as watched } from '../../../../../controllers/store/search/filters/watched';
import { AppSelector } from '../../../../quick-find/dialog/filters/inline-product-filters/app-selector';
import type { OnFilterChangeCallback } from '../../../../quick-find/dialog/filters/inline-product-filters/utils';
import AppSelectFilter from '../app-select';
import ArchivedFilter from '../archived';
import ArchivedAndPendingComponentsFilter from '../archived-and-pending-components';
import AssigneeFilter from '../assignee';
import CampaignStatusFilter from '../campaign-status';
import CaseStatusFilter from '../case-status';
import CloseDateFilter from '../close-date';
import CommentedByMeFilter from '../commented-by-me';
import ContentFilter from '../content';
import ContributorFilter from '../contributor';
import CreatedByFilter from '../created-by';
import FocusAreaTypeFilter from '../focus-area-type';
import FocusAreaUnderFilter from '../focus-area-under';
import LabelFilter from '../label';
import LastUpdatedFilter from '../last-updated';
import LastUpdatedByFilter from '../last-updated-by';
import OpportunityStatusFilter from '../opportunity-status';
import OwnerFilter from '../owner';
import PageStatusFilter from '../page-status';
import PositionEmploymentTypeFilter from '../position-employment-type';
import PositionFocusAreaFilter from '../position-focus-area';
import PositionJobFamilyFilter from '../position-job-family';
import PositionStatusFilter from '../position-status';
import PositionTeamFilter from '../position-team';
import ProjectFilter from '../project';
import PullRequestStatusFilter from '../pull-request-status';
import ReporterFilter from '../reporter';
import { ResultsSort } from '../results-sort';
import SalesforceStageFilter from '../salesforce-stage';
import SlackChannelFilter from '../slack-channel';
import SpaceFilter from '../space';
import StatusFilter from '../status';
import TitleMatchOnlyFilter from '../title-match-only';
import UniversalTypeFilter from '../universal-type';
import VerifiedFilter from '../verified';
import WatchedFilter from '../watched';

import { i18n } from './messages';

const dropdownContainerStyles = xcss({
	width: '312px',
});

const filtersContainerStyles = xcss({
	padding: 'space.200',
});

const clearButtonStylesOld = xcss({
	margin: 'space.200',
});

const clearButtonStyles = xcss({
	paddingInline: 'space.200',
	paddingBlock: 'space.300',
});

const filters = {
	[universalTypeFilterId]: UniversalTypeFilter,
	[lastUpdated.id]: LastUpdatedFilter,
	[createdBy.id]: CreatedByFilter,
	[contributor.id]: ContributorFilter,
	[lastUpdatedBy.id]: LastUpdatedByFilter,
	[owner.id]: OwnerFilter,
	[pageStatus.id]: PageStatusFilter,
	[space.id]: SpaceFilter,
	[focusAreaType.id]: FocusAreaTypeFilter,
	[focusAreaUnder.id]: FocusAreaUnderFilter,
	[assignee.id]: AssigneeFilter,
	[reporter.id]: ReporterFilter,
	[project.id]: ProjectFilter,
	[content.id]: ContentFilter,
	[label.id]: LabelFilter,
	[statusFilter.id]: StatusFilter,
	[positionStatus.id]: PositionStatusFilter,
	[positionEmploymentType.id]: PositionEmploymentTypeFilter,
	[positionJobFamily.id]: PositionJobFamilyFilter,
	[positionTeam.id]: PositionTeamFilter,
	[positionFocusArea.id]: PositionFocusAreaFilter,
	[salesforceStage.id]: SalesforceStageFilter,
	[campaignStatus.id]: CampaignStatusFilter,
	[caseStatus.id]: CaseStatusFilter,
	[closeDate.id]: CloseDateFilter,
	[opportunityStatus.id]: OpportunityStatusFilter,
	[pullRequestStatus.id]: PullRequestStatusFilter,
	[slackChannel.id]: SlackChannelFilter,
};

const checkboxFilters = {
	[watched.id]: WatchedFilter,
	[verified.id]: VerifiedFilter,
	[titleMatchOnly.id]: TitleMatchOnlyFilter,
	[archived.id]: ArchivedFilter,
	[archivedAndPendingComponents.id]: ArchivedAndPendingComponentsFilter,
	[commentedByMe.id]: CommentedByMeFilter,
};

const dividerStylesOld = xcss({
	borderBottomWidth: 'border.width',
	borderColor: 'color.border',
	borderStyle: 'solid',
	borderTop: 0,
	marginInline: 'space.200',
});

const dividerStyles = xcss({
	borderBottomWidth: 'border.width',
	borderColor: 'color.border',
	borderStyle: 'solid',
	borderTop: 0,
});

const badgeStyles = xcss({
	alignItems: 'center',
	alignSelf: 'center',
	display: 'flex',
});

const DropdownFilter = ({
	experience = SearchExperience.SEARCH_PAGE,
	onFiltersChange,
	onDropdownOpenChange,
	hideOnHoverOut = false,
	onClearOptions,
}: {
	experience?: SearchStoreExperience;
	onFiltersChange?: OnFilterChangeCallback;
	onDropdownOpenChange?: (isOpen: boolean) => void;
	hideOnHoverOut?: boolean;
	onClearOptions?: (params: { filterId: string }) => void;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [{ selectedFilterCount: selectedFilterCountWithoutProduct }] = useSelectedFilterCount();
	const { resetSort, clearAllFilters, unsetFilterDropdownOpen } = useSearchActions();
	const [selectedProduct] = useSelectedProduct();
	const [isAnyFilterSelected] = useAnyFilterSelected();
	const isConfluence = selectedProduct === ProductKeys.Confluence;
	const isJira = selectedProduct === ProductKeys.Jira;
	const filtersDiscoverability2Enabled = useFiltersDiscoverability2Enabled();
	const [primaryProduct] = usePrimaryProduct();
	const [features] = useFeatures();
	const isLiteMode = features?.lite?.enabled ?? false;
	// `useFiltersToDisplay` centrally applies FILTER_EXCLUSION_RULES + FILTER_INCLUSION_RULES
	// via `getExcludedFilterIds`, projecting both `filtersInLine` (consumed by ProductFilters)
	// and `filtersInDropdown` (consumed here). No further filtering is required at the call site.
	const [{ filtersInDropdown }] = useFiltersToDisplay({
		basedOnPrimaryProduct: experience === SearchExperience.QUICK_FIND,
		primaryProduct: primaryProduct,
	});

	// `useSelectedFilterCount` also applies FILTER_EXCLUSION_RULES + FILTER_INCLUSION_RULES
	// centrally, so we only need to add 1 here for the selected-product chip (when visible).
	const selectedFilterCount =
		selectedProduct && filtersDiscoverability2Enabled && !isLiteMode
			? selectedFilterCountWithoutProduct + 1
			: selectedFilterCountWithoutProduct;

	const [filterDropdownOpenByQueryParam] = useFilterDropdownOpenByQueryParam();
	const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);

	const handleDropdownOpenChange = useCallback(
		({ isOpen }: { isOpen: boolean }) => {
			setIsDropdownOpen(isOpen);
			onDropdownOpenChange?.(isOpen);
			if (!isOpen && filterDropdownOpenByQueryParam) {
				unsetFilterDropdownOpen();
			}
		},
		[onDropdownOpenChange, filterDropdownOpenByQueryParam, unsetFilterDropdownOpen],
	);

	return (
		<MaybeHoverOutWrapper
			enabled={hideOnHoverOut}
			callback={() => handleDropdownOpenChange({ isOpen: false })}
		>
			<DropdownMenu
				testId="dropdown-filter-menu"
				{...(filtersDiscoverability2Enabled && {
					isOpen: isDropdownOpen || filterDropdownOpenByQueryParam,
					onOpenChange: handleDropdownOpenChange,
				})}
				trigger={({ triggerRef, ...providedProps }) =>
					filtersDiscoverability2Enabled ? (
						<FilterButton
							ref={triggerRef}
							{...providedProps}
							isSelected={selectedFilterCount > 0 || isDropdownOpen}
							testId="dropdown-filter-button"
							aria-label={formatMessage(i18n.dropdownButtonLabel)}
							filterOrControlId="dropdown"
							hasChevron={false}
							iconAfter={
								<>
									{selectedFilterCount > 0 ? (
										<Box xcss={badgeStyles}>
											<Badge appearance="information">{selectedFilterCount}</Badge>
										</Box>
									) : null}
								</>
							}
						></FilterButton>
					) : (
						<FilterButtonOld
							otherProps={{ ...providedProps, ref: triggerRef }}
							isSelected={selectedFilterCount > 0}
							filterOrControlId="dropdown"
							selectionBadge={
								selectedFilterCount > 0 ? (
									<Badge appearance="information">{selectedFilterCount}</Badge>
								) : null
							}
							testId="dropdown-filter-button"
							ariaLabel={formatMessage(i18n.dropdownButtonLabel)}
						/>
					)
				}
				shouldRenderToParent
			>
				<Stack xcss={dropdownContainerStyles}>
					<Stack xcss={filtersContainerStyles} space="space.200">
						{filtersDiscoverability2Enabled &&
							!isLiteMode &&
							experience !== SearchExperience.QUICK_FIND && <AppSelectFilter isFilterInDropdown />}
						{experience === SearchExperience.QUICK_FIND && <AppSelector isFilterInDropdown />}
						{filtersInDropdown.map((filterId) => {
							// TODO: remove the typecast here
							// (was added so that product type filters didn't have to be included in the filters object)
							const Filter = filters[filterId as keyof typeof filters];
							if (!Filter) {
								return null;
							}
							return (
								<Filter
									key={filterId}
									isFilterInDropdown
									// Filters inside the dropdown should not close when hovering out
									hideOnHoverOut={false}
									onFilterChange={(change) =>
										onFiltersChange?.(
											filterId,
											change,
											experience === SearchExperience.QUICK_FIND
												? FILTER_CHANGE_LOCATION.DROPDOWN
												: FILTER_CHANGE_LOCATION.DROPDOWN_FPS,
										)
									}
									onClearOptions={onClearOptions}
								/>
							);
						})}
						{(isConfluence || isJira) && <ResultsSort isFilterInDropdown />}
						{filtersInDropdown
							.filter((id) => {
								if (experience === SearchExperience.QUICK_FIND) {
									return id !== watchedFilter.id && id !== commentedByMe.id;
								} else {
									return true;
								}
							})
							.map((filterId) => {
								const Filter = checkboxFilters[filterId as keyof typeof checkboxFilters];
								if (!Filter) {
									return null;
								}
								return (
									<Filter
										key={filterId}
										isFilterInDropdown
										onFilterChange={(change) =>
											onFiltersChange?.(
												filterId,
												change,
												experience === SearchExperience.QUICK_FIND
													? FILTER_CHANGE_LOCATION.DROPDOWN
													: FILTER_CHANGE_LOCATION.DROPDOWN_FPS,
											)
										}
										onClearOptions={onClearOptions}
									/>
								);
							})}
					</Stack>
					{isAnyFilterSelected && (
						<>
							{filtersDiscoverability2Enabled ? (
								<Box xcss={dividerStyles} />
							) : (
								<Box xcss={dividerStylesOld} />
							)}
							{filtersDiscoverability2Enabled ? (
								<ClearFilterButton
									xcss={clearButtonStyles}
									text={i18n.clearFilters}
									onClick={() => {
										// TODO: Consolidate these clear filter functions
										// Should we use clearAllAvailableFilters or clearAllFilters?
										// https://product-fabric.atlassian.net/browse/QS-5911
										clearAllFilters();
										resetSort();
									}}
								/>
							) : (
								<Box xcss={clearButtonStylesOld}>
									<Button
										onClick={() => {
											// TODO: Consolidate these clear filter functions
											// Should we use clearAllAvailableFilters or clearAllFilters?
											// https://product-fabric.atlassian.net/browse/QS-5911
											clearAllFilters();
											resetSort();
										}}
									>
										{formatMessage(i18n.clearFilters)}
									</Button>
								</Box>
							)}
						</>
					)}
				</Stack>
			</DropdownMenu>
		</MaybeHoverOutWrapper>
	);
};

export default DropdownFilter;
